import React, { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Tooltip as MuiTooltip } from '@mui/material';
import Modal from '../modal';
import { hasLineApprovalHistory, lineApprovalDetails, lineApprovalEntries } from '../../utils/line-approval-history';

const DOT_COLORS = { requested: '#6c757d', approved: 'var(--primary)', denied: 'var(--danger)', delegated: '#6c757d', waiting: '#adb5bd' };

const formatDate = (value, lang) => {
  const date = new Date(value);
  if (!value || Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat(`${lang || 'en'}-CA`, { dateStyle: 'medium', timeStyle: 'short' }).format(date);
};

const EntryList = ({ entries, data, clampReason }) => (
  <ol className="list-unstyled m-0 d-flex flex-column" style={{ gap: 10 }}>
    {entries.map((entry, index) => (
      <li key={index} className="d-flex" style={{ gap: 10 }}>
        <span aria-hidden="true" style={{ flex: 'none', width: 9, height: 9, borderRadius: 999, marginTop: 6, background: DOT_COLORS[entry.kind] }} />
        <div className="d-flex flex-column">
          <span>
            {data.t.read(`line_approval_history.${entry.kind}`)} <strong>{entry.person}</strong>
            {entry.onBehalfOf && <> {data.t.read('line_approval_history.on_behalf_of')} <strong>{entry.onBehalfOf}</strong></>}
            {entry.delegatedTo && <> {data.t.read('line_approval_history.delegated_to')} <strong>{entry.delegatedTo}</strong></>}
          </span>
          {entry.at && <small className="text-muted">{formatDate(entry.at, data.t.lang)}</small>}
          {entry.reason && (
            <span
              className="text-secondary"
              style={clampReason ? { display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' } : { whiteSpace: 'pre-wrap' }}
            >
              {entry.reason}
            </span>
          )}
        </div>
      </li>
    ))}
  </ol>
);

const Details = ({ text, data, clamp }) => (
  <div className="d-flex flex-column">
    <small className="text-muted font-weight-bold">{data.t.read('line_approval_history.details')}</small>
    <span style={clamp ? { display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden', whiteSpace: 'pre-wrap' } : { whiteSpace: 'pre-wrap' }}>
      {text}
    </span>
  </div>
);

const HistoryBody = ({ item, data, compact }) => {
  const entries = lineApprovalEntries(item);
  const details = lineApprovalDetails(item);
  return (
    <div className="d-flex flex-column" style={{ gap: 12 }}>
      {entries.length > 0 && <EntryList entries={entries} data={data} clampReason={compact} />}
      {details && <Details text={details} data={data} clamp={compact} />}
    </div>
  );
};

const CARD_SX = {
  bgcolor: '#fff',
  color: '#212529',
  border: '1px solid #dee2e6',
  borderRadius: '10px',
  boxShadow: '0 12px 32px rgba(0, 0, 0, 0.16)',
  fontSize: '0.8125rem',
  fontWeight: 400,
  maxWidth: 360,
  p: '14px 16px',
};

/**
 * Hovering the Approval cell shows the line's approval history;
 * clicking the card opens it in a modal. Lines with no history keep the cell as is.
 */
const LineApprovalHistory = ({ item, data, children }) => {
  const [cardOpen, setCardOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [boundary, setBoundary] = useState(null);
  const triggerRef = useRef(null);

  if (!hasLineApprovalHistory(item)) return children;

  const openModal = () => {
    setCardOpen(false);
    setModalOpen(true);
  };

  const card = (
    <div role="button" tabIndex={-1} className="d-flex flex-column pointer" style={{ gap: 12 }} onClick={openModal}>
      <strong style={{ fontSize: '0.875rem' }}>{data.t.read('line_approval_history.title')}</strong>
      <HistoryBody item={item} data={data} compact />
      <small className="text-muted border-top pt-2">{data.t.read('line_approval_history.click_to_enlarge')}</small>
    </div>
  );

  return (
    <>
      <MuiTooltip
        title={card}
        open={cardOpen && !modalOpen}
        onOpen={(event) => {
          // A dialog opened from the cell (the denial reason) is inside this trigger; the card stays shut over it.
          if (event?.target?.closest?.('.modal')) return;
          // Kept inside the invoice's scrolling area so a line near the bottom does not cover the footer buttons.
          // Read from a ref: the hover event reaches onOpen after enterDelay, when its currentTarget is gone.
          setBoundary(triggerRef.current?.closest?.('.invoice-editor-scroll') || null);
          setCardOpen(true);
        }}
        onClose={() => setCardOpen(false)}
        placement="left"
        enterDelay={150}
        arrow
        slotProps={{ tooltip: { sx: CARD_SX }, arrow: { sx: { color: '#fff', '&::before': { border: '1px solid #dee2e6' } } } }}
        // This MUI version builds the popper's modifiers from PopperProps only, not from slotProps.popper.
        PopperProps={boundary ? {
          popperOptions: {
            modifiers: [
              { name: 'preventOverflow', options: { boundary, altAxis: true, padding: 8 } },
              { name: 'flip', options: { boundary, fallbackPlacements: ['left-end', 'left-start', 'top', 'bottom'] } },
            ],
          },
        } : undefined}
      >
        <div ref={triggerRef} data-testid="line-approval-history-trigger" onClick={() => setCardOpen(false)}>{children}</div>
      </MuiTooltip>
      {/* Outside the table so the cell's styles and click handlers do not reach the modal. */}
      {modalOpen && createPortal(
        <div onClick={(event) => event.stopPropagation()}>
          <Modal
            title={data.t.read('line_approval_history.title')}
            cancelAction={() => setModalOpen(false)}
            cancelText={data.t.read('line_approval_history.close')}
          >
            <div className="d-flex flex-column" style={{ gap: 16 }}>
              {item.Description && <span className="text-muted">{item.Description}</span>}
              <HistoryBody item={item} data={data} compact={false} />
            </div>
          </Modal>
        </div>,
        document.body,
      )}
    </>
  );
};

export default LineApprovalHistory;
